<script setup>
import { computed, watch, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { OnClickOutside } from '@vueuse/components';
import useVuelidate from '@vuelidate/core';

import Spinner from 'next/spinner/Spinner.vue';
import Button from 'next/button/Button.vue';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import Breadcrumb from 'next/breadcrumb/Breadcrumb.vue';
import Editor from 'next/wgpt/Automation/editor/Index.vue';
import TaskEditorLayout from './TaskEditorLayout.vue';
import TaskEditorSettings from './TaskEditorSettings.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();
const taskId = computed(() => Number(route.params.taskId));
const initialState = {};
const state = reactive({ ...initialState });
const v$ = useVuelidate({}, state);
const showActivateActionMenu = ref(false);

const uiFlags = useMapGetter('wgptTasks/getUIFlags');

const task = computed(() => {
  return getters['wgptTasks/getTask'].value(taskId.value);
});

const hasPendingChanges = computed(() => {
  if (!task.value) return false;
  return (
    state.value?.name !== task.value.name ||
    state.value?.description !== task.value.description ||
    JSON.stringify(state.value?.steps) !== JSON.stringify(task.value.steps) ||
    JSON.stringify(state.value?.trigger) !== JSON.stringify(task.value.trigger)
  );
});

const isLoading = computed(() => {
  if (task.value === taskId.value) return false;
  return uiFlags.value.isFetchingItem;
});

const isTaskActive = computed(() => {
  return task.value?.active;
});

const executeMenuItems = computed(() => {
  return [
    {
      label: t('WGPT_TASKS.EDITOR.HEADER.DEACTIVATE_BUTTON'),
      value: 'deactivate',
      action: 'deactivate',
      disabled:
        isLoading.value ||
        uiFlags.value.isUpdating ||
        hasPendingChanges.value ||
        !isTaskActive.value,
    },
  ];
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('WGPT_TASKS.EDITOR.HEADER.BREADCRUMB.TASKS'),
      link: '#',
    },
  ];
  if (task.value?.name) {
    items.push({
      label: task.value.name,
    });
  }
  return items;
});

watch(
  taskId,
  (newTaskId, oldTaskId) => {
    if (!newTaskId) return;
    if (newTaskId === oldTaskId) return;
    store.dispatch('wgptTasks/show', newTaskId);
  },
  { immediate: true }
);

watch(
  task,
  (newTask, oldTask = {}) => {
    if (Object.keys(oldTask).length) return;
    state.value = {
      name: newTask.name,
      description: newTask.description,
      trigger: newTask.trigger,
      steps: newTask.steps,
    };
  },
  { immediate: true }
);

const handleSaveTask = async () => {
  try {
    if (v$.value.$invalid) {
      v$.value.$touch();
      throw new Error(t('WGPT_TASKS.EDITOR.API.ERROR_MESSAGE'));
    }

    const updateData = {
      id: taskId.value,
      wgpt_automation_attributes: {
        name: state.value.name,
        description: state.value.description,
        trigger: state.value.trigger,
        steps: state.value.steps,
      },
    };
    const updatedTask = await store.dispatch('wgptTasks/update', updateData);
    useAlert(t('WGPT_TASKS.EDITOR.API.SUCCESS_MESSAGE'));

    if (updatedTask) {
      state.value = {
        name: updatedTask.name,
        description: updatedTask.description,
        trigger: updatedTask.trigger,
        steps: updatedTask.steps,
      };
    }
  } catch (error) {
    useAlert(error?.message || t('WGPT_TASKS.EDITOR.API.ERROR_MESSAGE'));
  }
};

const goToTasksList = () => {
  if (window.history.state?.back) {
    router.back();
  } else {
    router.push(
      `/app/accounts/${route.params.accountId}/settings/wgpt-tasks/list`
    );
  }
};

const toggleActivate = async active => {
  if (isTaskActive.value === active) return;

  try {
    const id = taskId.value;
    const data = {
      wgpt_automation_attributes: {
        active,
      },
    };
    await store.dispatch('wgptTasks/update', { id, ...data });
    useAlert(t('WGPT_TASKS.EDITOR.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('WGPT_TASKS.EDITOR.API.ERROR_MESSAGE'));
  }
};

const handleActivateAction = ({ action }) => {
  showActivateActionMenu.value = false;
  if (action === 'deactivate') {
    toggleActivate(false);
  }
};
</script>

<template>
  <TaskEditorLayout>
    <template #start>
      <div class="flex items-center gap-5">
        <Breadcrumb :items="breadcrumbItems" @click="goToTasksList" />
      </div>
    </template>
    <template #end>
      <Button
        :label="t('WGPT_TASKS.EDITOR.HEADER.SAVE_BUTTON')"
        :is-loading="uiFlags.isUpdating"
        :disabled="isLoading || uiFlags.isUpdating || !hasPendingChanges"
        size="sm"
        class="group-hover/campaign-button:brightness-110"
        @click="handleSaveTask"
      />

      <div class="flex items-center">
        <Button
          :label="t('WGPT_TASKS.EDITOR.HEADER.ACTIVATE_BUTTON')"
          size="sm"
          class="ltr:rounded-r-none rtl:rounded-l-none"
          :disabled="
            isLoading || uiFlags.isUpdating || hasPendingChanges || isTaskActive
          "
          @click="toggleActivate(true)"
        />
        <div class="relative">
          <OnClickOutside @trigger="showActivateActionMenu = false">
            <Button
              icon="i-lucide-chevron-down"
              size="sm"
              :disabled="!taskId || hasPendingChanges || !isTaskActive"
              class="ltr:rounded-l-none rtl:rounded-r-none"
              @click.stop="showActivateActionMenu = !showActivateActionMenu"
            />
            <DropdownMenu
              v-if="showActivateActionMenu"
              :menu-items="executeMenuItems"
              class="mt-2 ltr:right-0 rtl:left-0 top-full"
              @action="handleActivateAction($event)"
            />
          </OnClickOutside>
        </div>
      </div>

      <router-link :to="{ query: { selected: 'settings' } }" replace>
        <Button
          variant="ghost"
          color="slate"
          icon="i-lucide-bolt"
          size="sm"
          :class="{
            'outline-none outline outline-1 outline-offset-1 outline-[#fd6262]':
              $route.query.selected === 'settings',
          }"
        />
      </router-link>
    </template>
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <Editor
      v-else
      v-model="state.value"
      :dirty="hasPendingChanges"
      class="h-full border-t border-n-weak"
      disable-trigger
    >
      <template #customSidebarSettings>
        <TaskEditorSettings v-model="state.value" />
      </template>
    </Editor>
  </TaskEditorLayout>
</template>
